import { defaultState } from "./defaults";
import type { AppState, Booking, FormSubmission, IntakeForm, EventType } from "./types";

const STORAGE_PREFIX = "morejpegs-scheduler-state-v5";

type Listener = (state: AppState) => void;

function cloneDefault(): AppState {
  return structuredClone(defaultState);
}

function normalizeState(parsed?: Partial<AppState>): AppState {
  const base = cloneDefault();
  if (!parsed) return base;
  return {
    ...base,
    ...parsed,
    branding: { ...base.branding, ...(parsed.branding ?? {}) },
    availability: { ...base.availability, ...(parsed.availability ?? {}) },
    eventTypes: (parsed.eventTypes ?? base.eventTypes).map((event) => ({
      ...event,
      intakeMode: event.intakeMode ?? "direct",
      intakeFormId: event.intakeFormId ?? "",
      externalFormUrl: event.externalFormUrl ?? "",
      externalFormPresentation: event.externalFormPresentation ?? "redirect"
    })),
    forms: parsed.forms ?? base.forms,
    bookings: parsed.bookings ?? [],
    submissions: parsed.submissions ?? []
  };
}

class SchedulerStore {
  private state: AppState = cloneDefault();
  private listeners = new Set<Listener>();
  private scope = "public";

  constructor() {
    this.state = this.load();
  }

  private storageKey(): string {
    return `${STORAGE_PREFIX}:${this.scope}`;
  }

  private load(): AppState {
    try {
      const raw = localStorage.getItem(this.storageKey());
      return raw ? normalizeState(JSON.parse(raw) as Partial<AppState>) : cloneDefault();
    } catch {
      return cloneDefault();
    }
  }

  setScope(scope: string): void {
    const next = scope || "public";
    if (next === this.scope) return;
    this.scope = next;
    this.state = this.load();
    this.notify();
  }

  private notify(): void {
    this.listeners.forEach((listener) => listener(this.getState()));
  }

  private persist(): void {
    localStorage.setItem(this.storageKey(), JSON.stringify(this.state));
    this.notify();
  }

  getState(): AppState {
    return structuredClone(this.state);
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  replaceState(state: AppState): void {
    this.state = normalizeState(state);
    this.persist();
  }

  replaceConfig(config: Omit<AppState, "bookings" | "submissions">): void {
    this.state.eventTypes = normalizeState(config).eventTypes;
    this.state.forms = config.forms ?? [];
    this.state.availability = config.availability;
    this.state.branding = config.branding;
    this.persist();
  }

  saveEventType(eventType: EventType): void {
    const index = this.state.eventTypes.findIndex((item) => item.id === eventType.id);
    if (index >= 0) this.state.eventTypes[index] = eventType;
    else this.state.eventTypes.unshift(eventType);
    this.persist();
  }

  deleteEventType(id: string): void {
    this.state.eventTypes = this.state.eventTypes.filter((item) => item.id !== id);
    this.state.forms = this.state.forms.map((form) => ({
      ...form,
      defaultAction: form.defaultAction.eventTypeId === id ? { ...form.defaultAction, eventTypeId: "" } : form.defaultAction,
      rules: form.rules.map((rule) => rule.action.eventTypeId === id ? { ...rule, action: { ...rule.action, eventTypeId: "" } } : rule)
    }));
    this.persist();
  }

  setEventActive(id: string, active: boolean): void {
    const item = this.state.eventTypes.find((event) => event.id === id);
    if (!item) return;
    item.active = active;
    item.updatedAt = new Date().toISOString();
    this.persist();
  }

  saveForm(form: IntakeForm): void {
    const index = this.state.forms.findIndex((item) => item.id === form.id);
    if (index >= 0) this.state.forms[index] = form;
    else this.state.forms.unshift(form);
    this.persist();
  }

  deleteForm(id: string): void {
    this.state.forms = this.state.forms.filter((item) => item.id !== id);
    this.state.eventTypes = this.state.eventTypes.map((event) => event.intakeFormId === id
      ? { ...event, intakeMode: "direct", intakeFormId: "", updatedAt: new Date().toISOString() }
      : event);
    this.persist();
  }

  setFormActive(id: string, active: boolean): void {
    const item = this.state.forms.find((form) => form.id === id);
    if (!item) return;
    item.active = active;
    item.updatedAt = new Date().toISOString();
    this.persist();
  }

  addBooking(booking: Booking): void {
    const index = this.state.bookings.findIndex((item) => item.id === booking.id || item.bookingId === booking.bookingId);
    if (index >= 0) this.state.bookings[index] = booking;
    else this.state.bookings.unshift(booking);
    this.persist();
  }

  replaceBookings(bookings: Booking[]): void {
    this.state.bookings = bookings;
    this.persist();
  }

  replaceSubmissions(submissions: FormSubmission[]): void {
    this.state.submissions = submissions;
    this.persist();
  }

  updateBookingStatus(id: string, status: Booking["status"]): void {
    const booking = this.state.bookings.find((item) => item.id === id || item.bookingId === id);
    if (!booking) return;
    booking.status = status;
    this.persist();
  }

  saveAvailability(availability: AppState["availability"]): void {
    this.state.availability = availability;
    this.persist();
  }

  saveBranding(branding: AppState["branding"]): void {
    this.state.branding = branding;
    this.persist();
  }

  reset(): void {
    this.state = cloneDefault();
    this.persist();
  }
}

export const store = new SchedulerStore();
